<template>
  <BaseModalLarge
    :heading="
      locationId
        ? $t('locations.update_location')
        : $t('locations.create_location')
    "
    :loading="loading || fiskalyLocationLoading"
    noPadding
    parentRoute="admin-locations"
  >
    <div :class="[$style.base, { [$style.smallScreen]: $screen === 's' }]">
      <div
        ref="scrollContainer"
        :class="$style.main"
      >
        <BaseForm
          :scrollContainer="$screen !== 's' ? scrollContainer : null"
          @submit="submitForm"
        >
          <Flex direction="column">
            <div :class="$style.name">
              <BaseInput
                v-model="location.internalName"
                :label="$t('locations.fields.internal_name')"
                required
                v-test="'location-internal-name'"
              />
              <div :class="$style.sortOrder">
                <BaseHeading size="xs">
                  {{ $t('locations.fields.sort_order') }}
                </BaseHeading>
                <BaseCounter
                  v-model="location.sortOrder"
                  :minValue="0"
                  v-test="'location-sort'"
                />
              </div>
            </div>
            <BaseInput
              v-model="location.name"
              :label="$t('locations.fields.name')"
              required
              v-test="'location-name'"
            />
            <BaseInput
              v-model="location.address"
              :label="$t('locations.fields.address')"
              v-test="'location-address'"
            />
            <div :class="$style.grouped">
              <BaseInput
                v-model="location.postalcode"
                :label="$t('locations.fields.postal_code')"
                v-test="'location-postcode'"
              />
              <BaseInput
                v-model="location.city"
                :label="$t('locations.fields.city')"
                v-test="'location-city'"
              />
            </div>
            <BaseInput
              v-model="location.phone"
              :label="$t('locations.fields.phone_number')"
              v-test="'location-phone'"
            />
            <BaseInput
              v-model="location.bankAccount"
              :label="$t('locations.fields.bank_account')"
              v-test="'location-bank'"
            />

            <template v-if="showFiskalyFields">
              <BaseHeading>{{ $t('locations.fiskaly') }}</BaseHeading>
              <BaseCheckbox
                v-model="prefillFromAddress"
                :label="$t('locations.prefill_fiskaly_address')"
                v-test="'fiskaly-prefill'"
              />
              <BaseAlert
                v-if="prefillFromAddress"
                size="s"
                color="warning"
                :text="$t('locations.fiskaly_address_warning')"
                v-test="'fiskaly-address-warning'"
              />
              <BaseInput
                v-model="fiskalyLocation.name"
                :label="$t('locations.fields.fiskaly.name')"
                required
                v-test="'fiskaly-name'"
              />
              <Address v-model="fiskalyLocation.address" />
              <BaseInput
                v-model="fiskalyLocation.label"
                :label="$t('integrations.fiskaly_fr.siret_number')"
                required
                :maxLength="14"
                :minLength="14"
                type="number"
                :placeholder="$t('integrations.fiskaly_fr.siret_number_placeholder')"
                v-test="'fiskaly-label'"
                :info="$t('integrations.fiskaly_fr.siret_number_info')"
              />
            </template>

            <BaseHeading size="s">{{ $t('locations.image') }}</BaseHeading>
            <BaseUpload
              preset="location-picture"
              withImagePreview
              withDelete
              :image="location.picture"
              @uploadResults="handleLogoUpload"
              @delete="deletePicture"
            />
          </Flex>
        </BaseForm>
      </div>

      <div :class="$style.side">
        <BaseHeading>{{ $t('locations.extra_settings') }}</BaseHeading>
        <BaseCard>
          <BaseCheckbox
            v-model="location.bookable"
            :label="$t('locations.fields.services')"
            :description="$t('locations.fields.services_description')"
            v-test="'location-bookable'"
          />
        </BaseCard>
      </div>
    </div>

    <template #footer>
      <BaseButton
        color="inverted"
        :disabled="updateMutationLoading || createMutationLoading"
        @click="goBack"
        v-test="'location-close'"
      >
        {{ $t('global.actions.close') }}
      </BaseButton>
      <BaseButton
        :loading="updateMutationLoading || createMutationLoading"
        :disabled="fiskalyLocationLoading"
        @click="submitForm"
        v-test="'location-submit'"
      >
        {{ $t(`global.actions.${locationId ? 'update' : 'create'}`) }}
      </BaseButton>
    </template>
  </BaseModalLarge>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import useVuelidate from '@vuelidate/core';
import { storeToRefs } from 'pinia';
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import type { CloudinaryResponse } from '@/components/base-upload/index.vue';
import { modal } from '@/helpers/ui';
import Address from '@/modules/admin/integrations/settings/fiskaly/Address.vue';
import { useLocationsStore } from '@/stores/locations';
import { useUserStore } from '@/stores/user';
import type { Location } from '@/types';
import { AddressLineType, Country } from '@/types';
import {
  CREATE_LOCATION,
  GET_FISKALY_LOCATION,
  GET_LOCATION,
  UPDATE_LOCATION,
} from '../graphql';

const v$ = useVuelidate();
const scrollContainer = ref();
const { t } = useI18n();

const { hasFeatureFlag } = useUserStore();
const showFiskalyFields = computed(() =>
  hasFeatureFlag('fiskaly-sign-enabled'),
);

const route = useRoute();
const router = useRouter();
const location = reactive({
  name: '',
  internalName: '',
  address: '',
  postalcode: '',
  city: '',
  bookable: false,
  picture: '',
  phone: '',
  bankAccount: '',
  sortOrder: 1,
});
const locationId = ref(Number.parseInt(route.params.id as string));

const fiskalyLocation = reactive({
  name: '',
  label: '',
  address: {
    country: Country.Fr,
    lineType: AddressLineType.StreetNumber,
    street: '',
    number: '',
    postalCode: '',
    city: '',
  },
});

const { onResult: onFiskalyLocationResult, loading: fiskalyLocationLoading } =
  useQuery(
    GET_FISKALY_LOCATION,
    () => ({ locationId: locationId.value }),
    () => ({
      enabled: !!locationId.value && hasFeatureFlag('fiskaly-sign-enabled'),
    }),
  );

onFiskalyLocationResult(({ data }) => {
  if (!data.fiskalyLocation) return;

  Object.assign(fiskalyLocation, {
    name: data.fiskalyLocation.name,
    label: data.fiskalyLocation.label,
    address: {
      country: Country.Fr,
      lineType: AddressLineType.StreetNumber,
      street: data.fiskalyLocation.address?.street,
      number: data.fiskalyLocation.address?.number,
      postalCode: data.fiskalyLocation.address?.postalCode,
      city: data.fiskalyLocation.address?.city,
    },
  });
});

const prefillFromAddress = ref(false);

watch(prefillFromAddress, (val) => {
  if (!val) return;
  const address = location.address?.trim() || '';
  // Street number may be at the start ("12 Rue de la Paix") or end ("Rue de la Paix 12")
  const leadingMatch = address.match(/^(\d+)\s+(.+)$/);
  const trailingMatch = address.match(/^(.+?)\s+(\d+)$/);
  fiskalyLocation.address.number = leadingMatch
    ? leadingMatch[1]
    : trailingMatch
      ? trailingMatch[2]
      : '';
  fiskalyLocation.address.street = leadingMatch
    ? leadingMatch[2]
    : trailingMatch
      ? trailingMatch[1]
      : address;
  fiskalyLocation.address.postalCode = location.postalcode || '';
  fiskalyLocation.address.city = location.city || '';
  fiskalyLocation.name = location.internalName || '';
});

const { onResult, loading } = useQuery(
  GET_LOCATION,
  () => ({ id: locationId.value }),
  () => ({ enabled: !!locationId.value }),
);

const { mutate: updateLocation, loading: updateMutationLoading } =
  useMutation(UPDATE_LOCATION);
const { mutate: createLocation, loading: createMutationLoading } =
  useMutation(CREATE_LOCATION);

const { locations } = storeToRefs(useLocationsStore());

onResult(({ data }) => {
  Object.assign(location, {
    id: data.location.id,
    name: data.location.name,
    internalName: data.location.internalName,
    address: data.location.address,
    postalcode: data.location.postalcode,
    city: data.location.city,
    bookable: data.location.bookable,
    picture: data.location.picture,
    phone: data.location.phone,
    bankAccount: data.location.bankAccount,
    sortOrder: data.location.sortOrder,
  });
});

const deletePicture = () => {
  location.picture = '';
};

const handleLogoUpload = (uploadResults: CloudinaryResponse) => {
  location.picture = uploadResults.secure_url;
};

const submitForm = () => {
  v$.value.$touch();

  if (v$.value.$invalid) {
    return;
  }

  if (locationId.value) {
    handleUpdateLocation();
  } else {
    handleCreateLocation();
  }
};

const locationInput = computed(() => {
  return showFiskalyFields.value
    ? {
        ...location,
        fiskalyLocation: {
          ...fiskalyLocation,
          label: fiskalyLocation.label.toString(),
        },
      }
    : location;
});

const handleUpdateLocation = () => {
  updateLocation({ input: locationInput.value }).then(
    ({ data: { updateLocation } }: any) => {
      if (updateLocation.location) {
        editLocation(updateLocation.location);
        goBack();
      }
    },
  );
};

const handleCreateLocation = () => {
  modal('confirmation', { message: t('locations.banner.confirmation') }).then(
    () => {
      createLocation({ input: locationInput.value }).then(
        ({ data: { createLocation } }: any) => {
          if (createLocation.location) {
            addLocation(createLocation.location);
            goBack();
          }
        },
      );
    },
  );
};

const editLocation = (location: Location) => {
  removeLocation(location.id);
  addLocation(location);
};

const removeLocation = (id: number) => {
  locations.value = locations.value.filter(
    (location: Location) => location.id !== id,
  );
};

const addLocation = (location: Location) => {
  locations.value.push(location);
};

const goBack = () => {
  router.push({ name: 'admin-locations' });
};
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;

  &:not(.smallScreen) {
    display: flex;
    align-items: stretch;
    gap: $spacing;
  }
}

.grouped {
  display: flex;
  gap: $spacing;

  & > * {
    width: 100%;
  }
}

.main {
  background-color: var(--color-bg);

  .base:not(.smallScreen) & {
    width: 65%;
    padding: $spacing * 1.5;
    overflow-y: auto;
  }

  .base.smallScreen & {
    padding: $spacing;
  }
}

.side {
  .base:not(.smallScreen) & {
    border-left: 1px solid var(--color-border);
    padding: $spacing * 1.5;
    width: 35%;
    overflow-y: auto;
  }

  .base.smallScreen & {
    padding: $spacing;
    border-top: 1px solid var(--color-border);
  }

  & > * {
    &:not(:last-child) {
      margin-bottom: $spacing;
    }
  }
}

.name {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;

  & > * {
    &:first-child {
      flex-grow: 1;
    }
  }
}

.sortOrder {
  display: flex;
  flex-direction: column;
  gap: $spacing * 0.5;
}
</style>
